import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { fetchApplicationInfoMapping } from '@services/applicationInfo';
import {
  fetchProductivityApps,
  fetchTeamProductivityApps,
} from '@services/productivityApps';
import { getAppNameByAppPath } from '@/utils/utility';
import { queryKeys } from '@config/queryKeys';

function fmtTime(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function calculate(appUsage, totalSec, focusApps, appNameMapping) {
  if (!appUsage || !focusApps?.length || !totalSec) return null;

  let focusSec = 0;
  for (const [path, secs] of Object.entries(appUsage)) {
    const name = getAppNameByAppPath(path, appNameMapping);
    if (focusApps.some((f) => f.toLowerCase() === name.toLowerCase())) {
      focusSec += secs;
    }
  }

  focusSec = Math.min(focusSec, totalSec);
  const otherSec = totalSec - focusSec;
  const focusPct = Math.round((focusSec / totalSec) * 100);

  return {
    focusPct,
    label: 'Focus',
    segments: [
      {
        name: 'Focus Time',
        pct: focusPct,
        time: fmtTime(focusSec),
        color: '#22c55e',
      },
      {
        name: 'Other Time',
        pct: 100 - focusPct,
        time: fmtTime(otherSec),
        color: '#E5E7EB',
      },
    ],
  };
}

/**
 * Resolves the effective focus app list (team override → company fallback),
 * normalises raw OS paths via getAppNameByAppPath, and returns the widget data.
 *
 * All three queries are React Query — parallel calls with the same key share
 * one in-flight request and a shared cache, so mounting this hook across
 * multiple dashboard components costs zero extra Firestore reads.
 *
 * @param {Record<string,number>|null|undefined} appUsage  raw path → seconds map
 * @param {number|null|undefined}                totalSec  total tracked seconds
 * @param {{ companyId?: string, teamId?: string }} ids
 * @returns {{ data: object|null, isLoading: boolean }}
 */
export function useFocusVsDistraction(
  appUsage,
  totalSec,
  { companyId, teamId } = {}
) {
  const { data: appNameMapping = {} } = useQuery({
    queryKey: queryKeys.applicationInfo.mapping,
    queryFn: fetchApplicationInfoMapping,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const { data: companyApps, isLoading: isLoadingCompany } = useQuery({
    queryKey: queryKeys.productivityApps.company(companyId),
    queryFn: () => fetchProductivityApps(companyId),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: teamApps } = useQuery({
    queryKey: queryKeys.productivityApps.team(teamId),
    queryFn: () => fetchTeamProductivityApps(teamId),
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const focusApps = useMemo(
    () =>
      teamApps?.hasOverride
        ? teamApps.focusApps
        : (companyApps?.focusApps ?? []),
    [teamApps, companyApps]
  );

  const data = useMemo(
    () => calculate(appUsage, totalSec, focusApps, appNameMapping),
    [appUsage, totalSec, focusApps, appNameMapping]
  );

  return { data, isLoading: !!companyId && isLoadingCompany };
}
